<template>
  <div class="space-y-6">
    <FormErrorSummary :errors="errorSummary" />

    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">
        List Name <span class="text-red-500 ml-0.5" aria-label="required">*</span>
      </label>
      <input
        :value="modelValue.title"
        @input="updateField('title', ($event.target as HTMLInputElement)?.value || '')"
        type="text"
        placeholder="Enter shopping list name..."
        class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-[16px] sm:text-sm"
        :class="errors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'"
      />
      <p v-if="errors.title" class="text-red-500 text-sm mt-1" role="alert">{{ errors.title }}</p>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Start Date <span class="text-red-500 ml-0.5" aria-label="required">*</span>
        </label>
        <Datepicker
          :model-value="modelValue.start_date"
          @update:model-value="updateField('start_date', $event)"
          :inputClass="[
            'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-base',
            errors.start_date ? 'border-red-500 bg-red-50' : 'border-gray-300'
          ].join(' ')"
          auto-apply
          :enable-time-picker="false"
          format="dd/MM/yyyy"
          :teleport="true"
          auto-position="top"
        />
        <p v-if="errors.start_date" class="text-red-500 text-sm mt-1" role="alert">{{ errors.start_date }}</p>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          End Date <span class="text-red-500 ml-0.5" aria-label="required">*</span>
        </label>
        <Datepicker
          :model-value="modelValue.end_date"
          @update:model-value="updateField('end_date', $event)"
          :inputClass="[
            'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-base',
            errors.end_date ? 'border-red-500 bg-red-50' : 'border-gray-300'
          ].join(' ')"
          auto-apply
          :enable-time-picker="false"
          format="dd/MM/yyyy"
          :teleport="true"
          auto-position="top"
        />
        <p v-if="errors.end_date" class="text-red-500 text-sm mt-1" role="alert">{{ errors.end_date }}</p>
      </div>
    </div>

    <!-- Explanation for sync action -->
    <div v-if="showSyncExplanation" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h3 class="font-semibold text-blue-800 mb-2">What does sync mean?</h3>
      <p class="text-blue-700 text-sm">
        Sync automatically creates a shopping list by gathering all the ingredients from the recipes
        of your meals within the selected date range. This saves you time by automatically collecting
        everything you need for your planned meals.
      </p>
    </div>

    <!-- Navigation buttons -->
    <div class="flex justify-between mt-6">
      <button type="button" @click="$emit('cancel')" class="btn-secondary">
        Cancel
      </button>
      <button type="button" @click="handleSubmit" class="btn-primary" :disabled="loading">
        <span v-if="loading">Creating...</span>
        <span v-else>{{ showSyncExplanation ? 'Next' : 'Create List' }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import FormErrorSummary from '../FormErrorSummary.vue'
import type { ValidationErrorItem } from '../FormErrorSummary.vue'

// Props
interface Props {
  modelValue: {
    title: string
    start_date: string
    end_date: string
  }
  errors: {
    title: string
    start_date: string
    end_date: string
  }
  submitAttempted?: boolean
  showSyncExplanation?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitAttempted: false,
  showSyncExplanation: false,
  loading: false
})

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: any]
  submit: []
  cancel: []
}>()

const errorSummary = computed<ValidationErrorItem[]>(() => {
  if (!props.submitAttempted) return []
  const items: ValidationErrorItem[] = []
  if (props.errors.title) items.push({ label: 'List Name', message: props.errors.title })
  if (props.errors.start_date) items.push({ label: 'Start Date', message: props.errors.start_date })
  if (props.errors.end_date) items.push({ label: 'End Date', message: props.errors.end_date })
  return items
})

// Methods
const updateField = (field: string, value: any) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [field]: value
  })
}

const handleSubmit = () => {
  if (!props.loading) {
    emit('submit')
  }
}
</script>
